import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import logo from "./img/enjuggernaut_logo.png";
import arrow_back from "./img/arrow_back_gray.svg";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import visibility from "./img/visibility.svg";
import visibility_off from "./img/visibility_off.svg";

export function Change() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [changeId, setChangeId] = useState<string>("");
  const [passwordVis, setPasswordVis] = useState<boolean>(false);

  const changeSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/\d/, "Password must contain at least one number")
      .matches(
        /[@$!%*?&#]/,
        "Password must contain at least one special character"
      )
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], "Passwords must match")
      .required("Please confirm your password"),
  });

  interface ChangeFormData {
    password: string;
    confirmPassword: string;
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangeFormData>({
    resolver: yupResolver(changeSchema),
  });

  async function submitChange(data: ChangeFormData) {
    const apiEndpoint =  "/api/change";

    const formData = new FormData();

    formData.append("change_id", changeId);
    formData.append("password", data.password);

    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        body: formData,
      });

      const responseData = await response.json();

      if (response.ok) {
        toast.success("Password changed successfully!");
        navigate("/login");
      } else {
        toast.error(responseData.error);
      }
    } catch (error) {
      toast.error("Error creating account!");
    }
  }

  useEffect(() => {
    if (!searchParams.has("reset_id")) {
      navigate("/");
    } else {
      const id: string | null = searchParams.get("reset_id");
      setChangeId(id == null ? "" : id);
    }
  }, []);

  return (
    <div className="light-panel fixed min-h-screen w-full">
      <button
        className="fixed ml-4 mt-4"
        onClick={() => {
          navigate("/");
        }}
      >
        <img src={arrow_back} />
      </button>

      <div className="mx-auto mt-8 flex w-4/5 flex-row rounded-md bg-[#252526] shadow-[0_10px_100px_-2px_rgba(0,0,0,0.3)] md:w-1/3">
        <div className="flex w-full flex-col rounded-md p-4 lg:p-6 xl:p-8 2xl:p-10">
          <img className="mx-auto w-4/5" alt="enjuggernaut logo" src={logo} />

          <form onSubmit={handleSubmit(submitChange)} className="w-full">
            <h1 className="pt-2 font-code text-lg font-bold text-[#CCCCC1] lg:text-xl xl:pt-4 xl:text-2xl">
              Password
            </h1>
            <div className="relative flex flex-row">
              <input
                {...register("password")}
                type={`${passwordVis ? "text" : "password"}`}
                className="mt-2 w-full p-2 pl-8 font-code text-base lg:text-lg xl:text-xl 2xl:p-4"
              />
              <button
                className="absolute right-0 top-4 pr-2 lg:top-5 lg:pr-4 xl:scale-150 2xl:top-7"
                onClick={() => setPasswordVis(!passwordVis)}
              >
                {!passwordVis && <img src={visibility}></img>}

                {passwordVis && <img src={visibility_off}></img>}
              </button>
            </div>
            {errors.password && (
              <p className="mt-2 font-code text-lg font-bold text-red-500">
                {errors.password.message}
              </p>
            )}

            <h1 className="pt-2 font-code text-lg font-bold text-[#CCCCC1] lg:text-xl xl:pt-4 xl:text-2xl">
              Retype Password
            </h1>
            <div className="relative flex flex-row">
              <input
                {...register("confirmPassword")}
                type={`${passwordVis ? "text" : "password"}`}
                className="mt-2 w-full p-2 font-code text-base lg:text-lg xl:text-xl 2xl:p-4"
              />
              <button
                className="absolute right-0 top-4 pr-2 lg:top-5 lg:pr-4 xl:scale-150 2xl:top-7"
                onClick={() => setPasswordVis(!passwordVis)}
              >
                {!passwordVis && <img src={visibility}></img>}

                {passwordVis && <img src={visibility_off}></img>}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-2 font-code text-lg font-bold text-red-500">
                {errors.confirmPassword.message}
              </p>
            )}

            <button
              className="start-button mx-auto mt-2 block w-1/2 p-3 font-code text-lg font-bold md:text-xl lg:text-2xl xl:mt-4 xl:text-3xl 2xl:mt-6 2xl:text-4xl"
              type="submit"
            >
              Change Password
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
